import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const STATUS_BADGE = {
  VERIFIED: { cls: "bg-success", label: "Verified" },
  MISSING: { cls: "bg-danger", label: "Missing" },
  DISCREPANCY: { cls: "bg-warning text-dark", label: "Discrepancy" },
  PENDING: { cls: "bg-secondary", label: "Pending" },
};

const AUDIT_STATUS_BG = { COMPLETED: "#f0fdf4", ONGOING: "#fffbeb", PENDING: "#f3f4f6" };
const AUDIT_STATUS_COLOR = { COMPLETED: "#198754", ONGOING: "#fd7e14", PENDING: "#6b7280" };

export default function ViewAuditRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [audit, setAudit] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [scanning, setScanning] = useState(null);
  const [formState, setFormState] = useState({});
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [activeTab, setActiveTab] = useState("systemNotes");

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);

    fetch(`${API_BASE_URL}/api/asset-audits/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 404) throw new Error("Audit record not found.");
        if (!r.ok) throw new Error("Failed to load audit record.");
        return r.json();
      })
      .then(data => setAudit(data))
      .catch(err => {
        toast.error(err.message || "Failed to load audit record.");
        navigate("/allauditrecords");
      })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-audits/${id}/items`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setItemsLoading(false); }
  }, [id, token]);

  useEffect(() => { if (token && id) loadItems(); }, [loadItems]);

  const openForm = item => {
    setFormState(prev => ({
      ...prev,
      [item.id]: prev[item.id] || { status: "VERIFIED", physicalCondition: "Good", actualLocation: item.expectedLocation || "", remarks: "" }
    }));
  };

  const submitScan = async itemId => {
    const f = formState[itemId];
    if (!f?.status) return;
    setScanning(itemId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-audits/${id}/items/${itemId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      if (!res.ok) throw new Error();
      toast.success("Item scanned.");
      await loadItems();
      // Refresh audit summary counts
      fetch(`${API_BASE_URL}/api/asset-audits/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(setAudit).catch(() => { });
    } catch { toast.error("Failed to save scan result."); }
    finally { setScanning(null); }
  };

  if (loading) return (
    <>
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid" style={{ paddingTop: 110 }}>
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      </div>
    </>
  );

  if (!audit) return null;

  const total = audit.totalItems || 0;
  const pending = audit.pendingItems || 0;
  const scanned = total - pending;
  const pct = total > 0 ? Math.round((scanned / total) * 100) : 0;
  const verified = audit.verifiedItems || 0;
  const missing = audit.missingItems || 0;
  const discrepancy = audit.discrepancyItems || 0;
  const auditStatus = audit.status || "PENDING";
  const isActive = auditStatus !== "COMPLETED";

  const filteredItems = filter === "ALL" ? items : items.filter(i => i.status === filter);

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h4 className="fw-bold mb-0"><i className="bi bi-clipboard2-check me-2"></i>{audit.auditName}</h4>
              <span className="badge" style={{ background: AUDIT_STATUS_BG[auditStatus], color: AUDIT_STATUS_COLOR[auditStatus], border: `1px solid ${AUDIT_STATUS_COLOR[auditStatus]}30`, fontSize: 12 }}>
                {auditStatus}
              </span>
            </div>
            <div className="d-flex gap-2 mt-1">
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/editauditrecord/${audit.id}`)}>
                Edit
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                Back
              </button>
            </div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              {audit.auditPeriod} &nbsp;·&nbsp; {audit.department || "All Departments"} &nbsp;·&nbsp; {audit.location || "All Locations"}
              &nbsp;·&nbsp; Created {audit.createdAt ? new Date(audit.createdAt).toLocaleDateString() : "—"}
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate("/allauditrecords")}>
            <i className="bi bi-list-ul me-1"></i> All Audits
          </button>
        </div>

        {/* Progress */}
        <div className="border rounded p-3 mb-3" style={{ background: "#fff" }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
              Progress: {scanned} / {total} scanned ({pct}%)
            </span>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{pending} remaining</span>
          </div>
          <div style={{ height: 10, background: "#e5e7eb", borderRadius: 5, overflow: "hidden", marginBottom: 12 }}>
            <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#16a34a" : "#2d5a9e", borderRadius: 5, transition: "width 0.3s" }} />
          </div>
          <div className="row g-2">
            {[
              { label: "Total", val: total, color: "#1e3a5f", icon: "bi-clipboard2" },
              { label: "Verified", val: verified, color: "#198754", icon: "bi-check-circle" },
              { label: "Missing", val: missing, color: "#dc3545", icon: "bi-x-circle" },
              { label: "Discrepancy", val: discrepancy, color: "#fd7e14", icon: "bi-exclamation-triangle" },
              { label: "Pending", val: pending, color: "#6b7280", icon: "bi-hourglass" },
            ].map(s => (
              <div className="col" key={s.label}>
                <div className="text-center p-2 border rounded" style={{ background: "#f9fafb" }}>
                  <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: 18 }}></i>
                  <div className="fw-bold" style={{ fontSize: 20, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Filter chips */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          {[
            ["ALL", `All (${total})`, "bg-light text-dark border"],
            ["PENDING", `Pending (${pending})`, "bg-secondary text-white"],
            ["VERIFIED", `Verified (${verified})`, "bg-success text-white"],
            ["MISSING", `Missing (${missing})`, "bg-danger text-white"],
            ["DISCREPANCY", `Discrepancy (${discrepancy})`, "bg-warning text-dark"],
          ].map(([key, label, cls]) => (
            <button key={key} className={`badge border-0 px-3 py-2 ${cls}`}
              onClick={() => setFilter(key)}
              style={{ cursor: "pointer", fontSize: 12, fontWeight: filter === key ? 700 : 400, outline: filter === key ? "2px solid #1e3a5f" : "none" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Items — scrollable list */}
        <div
          style={{
            maxHeight: "45vh",
            overflowY: "auto",
            overflowX: "hidden",
            borderRadius: 6,
            paddingRight: 4,
            scrollbarWidth: "thin",
            scrollbarColor: "#c1c9d2 transparent",
          }}
        >
          {itemsLoading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : filteredItems.length === 0 ? (
            <p className="text-muted text-center py-4">No items in this filter.</p>
          ) : (
            filteredItems.map(item => {
              const f = formState[item.id];
              const isPending = item.status === "PENDING";
              return (
                <div key={item.id} className="border rounded mb-2 p-3"
                  style={{ background: isPending ? "#fff" : item.status === "VERIFIED" ? "#f0fdf4" : item.status === "MISSING" ? "#fff1f2" : "#fffbeb" }}>
                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <div style={{ flex: 1 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontFamily: "monospace" }}>{item.assetCode}</span>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{item.assetName}</span>
                        <span className={`badge ${STATUS_BADGE[item.status]?.cls || "bg-secondary"}`} style={{ fontSize: 11 }}>
                          {STATUS_BADGE[item.status]?.label || item.status}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                        Dept: <strong>{item.expectedDepartment || "—"}</strong> &nbsp;·&nbsp;
                        Location: <strong>{item.expectedLocation || "—"}</strong> &nbsp;·&nbsp;
                        Assigned: <strong>{item.assignedToName || "Unassigned"}</strong>
                      </div>
                      {item.scannedAt && (
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                          Scanned at {new Date(item.scannedAt).toLocaleString()}
                          {item.physicalCondition && ` · Condition: ${item.physicalCondition}`}
                          {item.actualLocation && ` · Found at: ${item.actualLocation}`}
                          {item.remarks && ` · Remarks: ${item.remarks}`}
                        </div>
                      )}
                    </div>
                    {isActive && isPending && !f && (
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openForm(item)}>
                        <i className="bi bi-search me-1"></i>Scan
                      </button>
                    )}
                    {isActive && isPending && f && (
                      <button className="btn btn-sm btn-outline-secondary"
                        onClick={() => setFormState(prev => { const n = { ...prev }; delete n[item.id]; return n; })}>
                        Cancel
                      </button>
                    )}
                  </div>

                  {isActive && isPending && f && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="row g-2">
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: 12 }}>Result *</label>
                          <select className="form-select form-select-sm" value={f.status}
                            onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], status: e.target.value } }))}>
                            <option value="VERIFIED">✓ Verified</option>
                            <option value="MISSING">✗ Missing</option>
                            <option value="DISCREPANCY">⚠ Discrepancy</option>
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: 12 }}>Physical Condition</label>
                          <select className="form-select form-select-sm" value={f.physicalCondition}
                            onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], physicalCondition: e.target.value } }))}>
                            <option>Good</option><option>Fair</option><option>Damaged</option><option>Not Found</option>
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: 12 }}>Actual Location</label>
                          <input className="form-control form-control-sm" value={f.actualLocation}
                            onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], actualLocation: e.target.value } }))}
                            placeholder="Where found" />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                          <button className="btn btn-sm btn-primary " disabled={scanning === item.id} onClick={() => submitScan(item.id)}>
                            {scanning === item.id ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : "Save "}
                          </button>
                        </div>
                        {(f.status === "MISSING" || f.status === "DISCREPANCY") && (
                          <div className="col-12">
                            <label className="form-label" style={{ fontSize: 12 }}>Remarks *</label>
                            <textarea className="form-control form-control-sm" rows={2} value={f.remarks}
                              onChange={e => setFormState(prev => ({ ...prev, [item.id]: { ...prev[item.id], remarks: e.target.value } }))}
                              placeholder="Describe the issue..." />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* System Information Tabs */}
        <ul className="nav nav-tabs mb-0 fw-semibold mt-3" style={{ backgroundColor: navbarColor }}>
          {[["systemNotes", "System Notes"]].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
                style={{
                  color: activeTab === key ? navbarColor : "#fff",
                  backgroundColor: activeTab === key ? "#fff" : "transparent",
                  fontWeight: activeTab === key ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <div className="border p-3">
          <div className="text-muted small mb-2">
            <strong>Date Created:</strong>{" "}
            {audit.createdAt ? new Date(audit.createdAt).toLocaleString() : "—"}
          </div>
          {activeTab === "systemNotes" && (
            <SystemNotes entityType="AUDIT" entityId={audit.id} token={token} />
          )}
        </div>
      </div>
    </>
  );
}
